import type { QueryClient } from '@tanstack/react-query';
import type { Container, Folder } from '@/types/domain';
import type { Tab } from '@/stores/treeStore';

// Resolve a tab's display label using whatever React Query has already cached.
// We never trigger fetches here — the data shown is whatever the user has
// already loaded for the active session.

/**
 * Un onglet dont la recherche est active AFFICHE des résultats de recherche, pas
 * son projet : son libellé doit le dire. Il gardait jusqu'ici le nom du projet
 * d'où la recherche avait été lancée, ce qui désignait un contenu qui n'était plus
 * à l'écran.
 *
 * Le terme est inclus dans le libellé (et pas seulement « Recherche ») : deux
 * onglets de recherche ouverts en même temps seraient sinon indistinguables. Le
 * CSS tronque, l'infobulle donne le terme complet.
 */
function searchLabel(searchQuery: string | undefined): string | null {
  const q = searchQuery?.trim();
  return q ? `Recherche : ${q}` : null;
}

/**
 * Titre d'un onglet resté sur l'arbre. Cette page montre les favoris, les projets
 * récents et les espaces de service — un point de départ, pas un élément.
 */
export const HOME_TAB_LABEL = 'Accueil';

export function computeTabLabel(
  tab: Tab,
  queryClient: QueryClient,
  /** Terme de recherche DE CET ONGLET (`uiStore.searchByTab[tab.id]`). */
  searchQuery?: string,
): string {
  const searching = searchLabel(searchQuery);
  if (searching) return searching;

  // L'ARBRE EST L'ACCUEIL, et son titre ne doit pas suivre la sélection.
  //
  // Le libellé se calculait sur `tab.folderId`, or ce champ est écrit par
  // `setActiveFolder` À CHAQUE CLIC dans l'arbre : sélectionner « 02 - APS »
  // renommait l'onglet « 02 - APS » alors que la page affichée n'avait pas
  // changé d'un pixel — elle montrait toujours la liste des favoris. Le titre
  // désignait donc un élément simplement surligné, pas le contenu de l'onglet.
  //
  // On se cale désormais sur `mode`, qui est ce qui décide réellement de la page
  // rendue (cf. TaskPaneShell), et sur `detailFolderId`, la cible d'une
  // navigation VOULUE — pas sur la sélection.
  if (tab.mode !== 'detail') return HOME_TAB_LABEL;

  if (tab.detailFolderId && tab.containerId) {
    const folders = queryClient.getQueryData<Folder[]>(['folders', tab.containerId]);
    const folder = folders?.find((f) => f.id === tab.detailFolderId);
    if (folder) return folder.name;
  }
  if (tab.containerId) {
    const containers = queryClient.getQueryData<Container[]>(['containers']);
    const container = containers?.find((c) => c.id === tab.containerId);
    if (container) return container.name;
  }
  // Mode détail mais cible non encore en cache : neutre plutôt que trompeur.
  return 'Nouvel onglet';
}

// Build a "Project › Folder › Subfolder" path string for the tooltip. Falls
// back to the short label when nothing else is cached.
export function computeTabPath(
  tab: Tab,
  queryClient: QueryClient,
  searchQuery?: string,
): string {
  // Le terme complet, en clair : c'est lui que le libellé tronque. On ne
  // mentionne PAS le projet de l'onglet — la portée de la recherche peut être
  // « Partout », et annoncer un projet serait alors faux.
  const q = searchQuery?.trim();
  if (q) return `Recherche : « ${q} »`;
  // Même raison que pour le libellé : sur l'arbre, l'infobulle décrivait le
  // chemin de l'élément SÉLECTIONNÉ, donc elle promettait un contenu que
  // l'onglet n'affichait pas.
  if (tab.mode !== 'detail') return HOME_TAB_LABEL;
  if (!tab.containerId) return 'Nouvel onglet';
  const containers = queryClient.getQueryData<Container[]>(['containers']);
  const container = containers?.find((c) => c.id === tab.containerId);
  const containerName = container?.name ?? '(projet inconnu)';
  if (!tab.detailFolderId) return containerName;

  const folders = queryClient.getQueryData<Folder[]>(['folders', tab.containerId]);
  if (!folders || folders.length === 0) return containerName;
  const byId = new Map(folders.map((f) => [f.id, f]));

  const chain: string[] = [];
  let cursor: string | null = tab.detailFolderId;
  const seen = new Set<string>();
  while (cursor && !seen.has(cursor)) {
    const f = byId.get(cursor);
    if (!f) break;
    chain.unshift(f.name);
    seen.add(cursor);
    cursor = f.parentId;
  }
  return [containerName, ...chain].join(' › ');
}
